/**
 * Ordered JSON serializer.
 *
 * V8 sorts integer-indexed object keys ascending on iteration, which would
 * render FDI tooth IDs as "11", "12", ..., "48" — losing the clinical UNN
 * sequence (UR8 → UR1 → UL1 → UL8 → LL8 → LL1 → LR1 → LR8). This helper
 * walks the value tree and, whenever it sees an object whose keys are all
 * FDI tooth IDs, emits them in UNN order. Other objects keep insertion order.
 *
 * The output is byte-equivalent to JSON.stringify for non-FDI-keyed objects.
 */

// FDI tooth IDs listed in Universal Numbering System (UNN) order.
//   UNN 1–32  → permanent teeth (UR3rdMolar → LR3rdMolar)
//   UNN A–T   → primary teeth   (UR2ndMolar → LR2ndMolar)
const FDI_ORDER_BY_UNN: ReadonlyArray<string> = [
  // Permanent — UNN 1-8 (Upper Right)
  "18",
  "17",
  "16",
  "15",
  "14",
  "13",
  "12",
  "11",
  // Permanent — UNN 9-16 (Upper Left)
  "21",
  "22",
  "23",
  "24",
  "25",
  "26",
  "27",
  "28",
  // Permanent — UNN 17-24 (Lower Left)
  "38",
  "37",
  "36",
  "35",
  "34",
  "33",
  "32",
  "31",
  // Permanent — UNN 25-32 (Lower Right)
  "41",
  "42",
  "43",
  "44",
  "45",
  "46",
  "47",
  "48",
  // Primary — UNN A-E (Upper Right)
  "55",
  "54",
  "53",
  "52",
  "51",
  // Primary — UNN F-J (Upper Left)
  "61",
  "62",
  "63",
  "64",
  "65",
  // Primary — UNN K-O (Lower Left)
  "75",
  "74",
  "73",
  "72",
  "71",
  // Primary — UNN P-T (Lower Right)
  "81",
  "82",
  "83",
  "84",
  "85",
];

const FDI_INDEX: ReadonlyMap<string, number> = new Map(
  FDI_ORDER_BY_UNN.map((fdi, i) => [fdi, i]),
);

function compareFdiByUnn(a: string, b: string): number {
  const ai = FDI_INDEX.get(a) ?? Number.MAX_SAFE_INTEGER;
  const bi = FDI_INDEX.get(b) ?? Number.MAX_SAFE_INTEGER;
  return ai - bi;
}

function isFdiKeyedObject(keys: string[]): boolean {
  if (keys.length === 0) return false;
  for (const k of keys) {
    if (!FDI_INDEX.has(k)) return false;
  }
  return true;
}

/**
 * Custom JSON serializer that orders FDI-keyed objects by UNN sequence.
 *
 * @param value - value to serialize
 * @param indent - number of spaces for pretty-print (0 = compact)
 */
export function stringifyOrdered(value: unknown, indent: number = 0): string {
  const out = serialize(value, indent, 0);
  return out ?? "null";
}

function serialize(
  value: unknown,
  indent: number,
  depth: number,
): string | undefined {
  if (value === undefined) return undefined;
  if (value === null) return "null";

  const t = typeof value;
  if (t === "string") return JSON.stringify(value);
  if (t === "number") return Number.isFinite(value) ? String(value) : "null";
  if (t === "boolean") return String(value);

  if (Array.isArray(value)) {
    return serializeArray(value, indent, depth);
  }

  if (t === "object") {
    return serializeObject(value as Record<string, unknown>, indent, depth);
  }

  return undefined;
}

function serializeArray(
  arr: unknown[],
  indent: number,
  depth: number,
): string {
  if (arr.length === 0) return "[]";
  const items = arr.map(v => serialize(v, indent, depth + 1) ?? "null");

  if (indent === 0) return `[${items.join(",")}]`;

  const innerPad = "\n" + " ".repeat(indent * (depth + 1));
  const outerPad = "\n" + " ".repeat(indent * depth);
  return `[${innerPad}${items.join("," + innerPad)}${outerPad}]`;
}

function serializeObject(
  obj: Record<string, unknown>,
  indent: number,
  depth: number,
): string {
  let keys = Object.keys(obj);
  if (isFdiKeyedObject(keys)) {
    keys = [...keys].sort(compareFdiByUnn);
  }

  const colon = indent > 0 ? ": " : ":";
  const items: string[] = [];
  for (const k of keys) {
    const serialized = serialize(obj[k], indent, depth + 1);
    if (serialized === undefined) continue;
    items.push(`${JSON.stringify(k)}${colon}${serialized}`);
  }

  if (items.length === 0) return "{}";

  if (indent === 0) return `{${items.join(",")}}`;

  const innerPad = "\n" + " ".repeat(indent * (depth + 1));
  const outerPad = "\n" + " ".repeat(indent * depth);
  return `{${innerPad}${items.join("," + innerPad)}${outerPad}}`;
}
